import MlxDialog from '../../../../../../components/common/MlxDialog/MlxDialog';
import MlxButton from '../../../../../../components/common/MlxButton/MlxButton';
import { JTranslation } from '../../../../../../helpers/jTranslationHelper';
import { CANCEL, CONTINUE, MENU_IMPORT_DIALOG_OK } from '../../../../../../constants/strings';

export type _ImportMessage = {
    variant: 'warning' | 'error' | 'success';
    title: string;
    message: string;
    // Appended after the message as plain data (for example a reset date); never translated.
    detail?: string;
    // A translated label and a plain number rendered after the detail (for example "Items added: 3").
    countLabel?: string;
    count?: number;
    // A translated closing sentence rendered last.
    note?: string;
    // Button labels (string constants). A success or error dialog shows two buttons only when a secondary label is given.
    primaryLabel?: string;
    secondaryLabel?: string;
};

type _ImportMessageDialogProps = {
    message: _ImportMessage | null;
    onPrimary: () => void;
    onSecondary?: () => void;
};

/** A warning offers Continue and Cancel; an error or success offers OK, or the labels the message names. */
const ImportMessageDialog = ({ message, onPrimary, onSecondary }: Readonly<_ImportMessageDialogProps>) => {
    const isWarning = message?.variant === 'warning';
    const secondaryLabel = message?.secondaryLabel ?? (isWarning ? CANCEL : undefined);
    const primaryLabel = message?.primaryLabel ?? (isWarning ? CONTINUE : MENU_IMPORT_DIALOG_OK);

    return (
        <MlxDialog
            visible={!!message}
            onHide={secondaryLabel ? (onSecondary ?? onPrimary) : onPrimary}
            header={message ? <JTranslation text={message.title} /> : undefined}
            footer={
                <div className="d-flex justify-content-end gap-2">
                    {secondaryLabel && (
                        <MlxButton outlined onClick={onSecondary}>
                            <JTranslation text={secondaryLabel} />
                        </MlxButton>
                    )}
                    <MlxButton onClick={onPrimary}>
                        <JTranslation text={primaryLabel} />
                    </MlxButton>
                </div>
            }
        >
            {message && (
                <p className="m-0">
                    <JTranslation text={message.message} />
                    {message.detail ? ` ${message.detail}` : ''}
                    {message.countLabel && message.count !== undefined && (
                        <>
                            {'. '}
                            <JTranslation text={message.countLabel} /> {message.count}
                        </>
                    )}
                    {message.note && (
                        <>
                            {'. '}
                            <JTranslation text={message.note} />
                        </>
                    )}
                </p>
            )}
        </MlxDialog>
    );
};

export default ImportMessageDialog;
